export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  errors?: { field: string; message: string }[];
  request_id?: string;
}

export interface TokenOut { access_token: string; token_type: string; expires_in: number }
export interface SignupOut { status: "active" | "pending_approval"; message: string | null; access_token: string | null; expires_in: number | null }

export interface Role { id: string; key: string; name: string; permissions: string[]; record_scope: string }
export interface User {
  id: string; email: string; full_name: string; is_active: boolean; role: Role;
  last_login_at: string | null; created_at: string;
  timezone: string | null; locale: string | null; dealer_name?: string | null; phone?: string | null;
}
export interface Tenant {
  id: string; slug: string; name: string; industry_pack: string; plan: string;
  currency: string; timezone: string;
}
export interface Me { user: User; tenant: Tenant; permissions: string[] }

export interface Country {
  code: string; name: string; currency: string; tax_name: string; tax_id_label: string;
  item_code_label: string; standard_rate: string; region: "EU" | "GCC" | null;
  states: { code: string; name: string }[];
}

export type FieldType =
  | "text" | "long_text" | "number" | "boolean" | "date" | "email" | "url" | "phone"
  | "picklist" | "multi_picklist";

export interface FieldDefinition {
  id: string; object_type: string; key: string; label: string; field_type: FieldType;
  options: string[]; required: boolean; help_text: string | null; position: number;
}

export type CustomValues = Record<string, string | number | boolean | string[] | null>;

export interface Contact {
  id: string; first_name: string; last_name: string | null; full_name: string;
  email: string | null; phone: string | null; job_title: string | null;
  company_name: string | null; owner: { id: string; full_name: string } | null;
  account: { id: string; name: string } | null;
  custom: CustomValues; created_at: string; updated_at: string;
  source: string; email_opt_out: boolean; email_opt_out_at: string | null;
  whatsapp_opt_in: boolean; whatsapp_opt_in_at: string | null; consent_source: string | null;
}

export interface Page<T> { items: T[]; next_cursor: string | null }

export interface AuditEntry {
  id: number; action: string; object_type: string; object_uid: string;
  changes: Record<string, { from: unknown; to: unknown } | unknown>;
  request_id: string | null; created_at: string;
}

export interface Ref { id: string; name: string }
export interface UserRef { id: string; full_name: string }

export type AccountType = "prospect" | "customer" | "dealer" | "distributor" | "supplier" | "partner";
export const ACCOUNT_TYPES: AccountType[] = ["prospect", "customer", "dealer", "distributor", "supplier", "partner"];

export interface Account {
  id: string; name: string; account_type: AccountType; industry: string | null;
  website: string | null; phone: string | null; email: string | null; tax_id: string | null;
  address_line: string | null; city: string | null; state: string | null; country: string | null;
  postal_code: string | null; owner: UserRef | null; parent: Ref | null; custom: CustomValues;
  created_at: string; updated_at: string;
  customer_group: string | null; price_book: Ref | null; territory: Ref | null; ledger_name: string | null;
  latitude: number | null; longitude: number | null; outstanding: number | null; outstanding_at: string | null;
  credit_limit: number | null;
  vat_check?: { valid: boolean; number: string; name: string | null; address: string | null; consultation: string | null; checked_at: string } | null;
}

export type StageKind = "open" | "won" | "lost";
export interface Stage { id: string; name: string; position: number; probability: number; kind: StageKind; rot_days: number | null }
export interface Pipeline { id: string; name: string; is_default: boolean; stages: Stage[] }

export interface Deal {
  id: string; name: string; pipeline: Ref; stage: { id: string; name: string; kind: StageKind; probability: number };
  account: Ref | null; dealer: Ref | null; contact: UserRef | null; owner: UserRef | null; amount: number; currency: string;
  expected_close_date: string | null; status: StageKind; lost_reason: string | null; closed_at: string | null;
  stage_changed_at: string; days_in_stage: number; custom: CustomValues; created_at: string; updated_at: string;
  forecast_category?: "pipeline" | "best_case" | "commit" | "omitted"; score?: number | null; score_reasons?: string[];
}

export interface BoardColumn { stage: Stage; count: number; total_amount: number; deals: Deal[] }
export interface Board { pipeline: Pipeline; columns: BoardColumn[]; open_count: number; open_amount: number; weighted_amount: number }

export type ActivityKind = "note" | "call" | "meeting" | "visit" | "email" | "whatsapp" | "task";
export interface Activity {
  id: string; kind: ActivityKind; subject: string; body: string | null; outcome: string | null;
  occurred_at: string; due_at: string | null; completed_at: string | null; duration_minutes: number | null;
  owner: UserRef | null; created_by: UserRef | null; contact: UserRef | null; account: Ref | null;
  deal: Ref | null; created_at: string; is_overdue: boolean;
}

export interface TimelineItem {
  type: "activity" | "change"; at: string; activity: Activity | null;
  change: { action: string; actor: string | null; changes: Record<string, { from: unknown; to: unknown }> } | null;
}

export type ImportObject = "contact" | "account" | "deal" | "product";
export interface ImportResult {
  object_type: ImportObject; dry_run: boolean; total_rows: number; created: number; skipped: number;
  columns: { header: string; field: string | null }[]; errors: { row: number; field: string | null; message: string }[];
  stages_found: string[]; unmatched_owners: string[];
}
export type ImportSource = "generic" | "hubspot" | "zoho_crm" | "pipedrive";

export interface SearchHit { type: "contact" | "account" | "deal"; id: string; title: string; subtitle: string | null }

export interface Product {
  id: string; name: string; sku: string; hsn_code: string | null; category: string | null; uom: string;
  unit_price: number; currency: string; tax_rate: number; description: string | null; is_active: boolean;
  custom: CustomValues; created_at: string; updated_at: string;
  /** Only for people who manage pricing; null for everyone else. */
  cost_price: number | null; min_margin_pct: number | null;
}

export type QuoteStatus = "draft" | "sent" | "accepted" | "rejected" | "superseded" | "expired";
/** intra, inter, export, domestic, reverse_charge, us_sales_tax, us_no_nexus, ca_<province>… */
export type TaxMode = string;
export interface TaxLine { label: string; amount: number }

export interface QuoteLine {
  id: string; position: number; description: string; sku: string | null; hsn_code: string | null; uom: string;
  quantity: number; unit_price: number; discount_pct: number; tax_rate: number;
  net_amount: number; tax_amount: number; line_total: number;
  /** Team view only (not on the customer's copy). */
  product_id?: string | null; list_price?: number | null; price_source?: string | null; uom_factor?: number; margin_pct?: number | null;
}

export type ApprovalStatus = "not_required" | "required" | "pending" | "approved" | "rejected";

export interface QuoteSummary {
  id: string; number: string; version: number; is_latest: boolean; status: QuoteStatus; display_status: QuoteStatus;
  title: string; currency: string; issue_date: string; valid_until: string; expires_at: string; grand_total: number;
  sent_at: string | null; decided_at: string | null; updated_at: string; account: Ref | null; deal: Ref | null; owner: UserRef | null;
  approval_status: ApprovalStatus;
}

export interface Quote extends QuoteSummary {
  contact: UserRef | null; payment_terms: string | null; delivery_terms: string | null; notes: string | null;
  terms: string | null; tax_mode: TaxMode; tax_mode_label: string; tax_lines: TaxLine[]; tax_note: string | null;
  item_code_label: string; gross: number; discount_total: number; subtotal: number;
  cgst: number; sgst: number; igst: number; tax_total: number; decided_by_name: string | null;
  customer_comment: string | null; public_url: string; lines: QuoteLine[];
  emails: { to_addresses: string[]; subject: string; status: string; sent_at: string | null; created_at: string }[];
  versions: QuoteSummary[];
  approval_reasons: string[]; approval_requested_at: string | null; approval_decided_at: string | null;
  approval_comment: string | null; approver_name: string | null; order_id: string | null;
}

export interface Seller {
  name: string; legal_name: string | null; tax_id: string | null; address_line: string | null; city: string | null;
  state: string | null; postal_code: string | null; country: string | null; phone: string | null; email: string | null;
  website: string | null;
}

export interface PublicQuote {
  number: string; version: number; title: string; currency: string; issue_date: string; valid_until: string;
  display_status: QuoteStatus; payment_terms: string | null; delivery_terms: string | null; notes: string | null;
  terms: string | null; tax_mode: TaxMode; tax_mode_label: string; tax_lines: TaxLine[]; tax_note: string | null;
  item_code_label: string; gross: number; discount_total: number; subtotal: number; cgst: number;
  sgst: number; igst: number; tax_total: number; grand_total: number; decided_by_name: string | null;
  decided_at: string | null; customer: string | null; customer_tax_id: string | null;
  seller_tax_id_label: string; buyer_tax_id_label: string; seller: Seller; lines: QuoteLine[];
}

export interface CompanyProfile extends Seller {
  currency: string; quote_prefix: string; quote_validity_days: number; quote_terms: string | null;
  us_sales_tax_rates: Record<string, string>; timezone: string;
  ai_enabled?: boolean; service_settings?: { sla_hours?: Partial<Record<"low" | "normal" | "high" | "urgent", number>>; warranty_months?: number };
  business_hours?: BusinessHours | null;
}
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export interface BusinessHours { days: Partial<Record<Weekday, [string, string] | null>>; holidays: string[] }

export interface Dashboard {
  currency: string; open_count: number; open_amount: number; weighted_amount: number;
  won_this_month_amount: number; won_this_month_count: number; win_rate_90d: number | null;
  avg_won_deal_90d: number | null; quotes_sent_30d: number; quotes_accepted_30d: number;
  quote_value_sent_30d: number; overdue_tasks: number; tasks_due_today: number;
  pipeline_by_stage: { stage: string; kind: StageKind; count: number; amount: number }[];
  results_by_month: { month: string; won_amount: number; won_count: number; lost_count: number }[];
  activities_7d: { kind: ActivityKind; count: number }[];
  top_open_deals: Deal[];
  open_orders: number; open_orders_value: number; overdue_deliveries: number; approvals_pending: number; receivables: number;
}

export interface ApiKey { id: string; name: string; prefix: string; last_used_at: string | null; expires_at: string | null; revoked_at: string | null; created_at: string }
export interface Webhook { id: string; url: string; description: string | null; events: string[]; is_active: boolean; created_at: string }
export interface WebhookDelivery {
  id: string; event_type: string; status: "pending" | "succeeded" | "failed"; attempts: number; response_status: number | null;
  last_error: string | null; duration_ms: number | null; created_at: string; delivered_at: string | null;
  next_attempt_at: string; payload: Record<string, unknown>;
}

// ---- Phase 5: CPQ, orders, accounting, territories, dealers, visits ---------------------------

export interface PriceBook {
  id: string; name: string; description: string | null; currency: string; customer_group: string | null;
  region_country: string | null; region_states: string[]; priority: number; valid_from: string | null;
  valid_until: string | null; is_active: boolean; entry_count: number; account_count: number; updated_at: string;
}
export interface PriceBookEntry {
  product_id: string; product_name: string; sku: string; uom: string; catalogue_price: number;
  min_quantity: number; unit_price: number;
}
export interface PriceBookDetail extends PriceBook { entries: PriceBookEntry[] }
export interface ProductUnit { uom: string; factor: number }
export interface CpqPolicy { discount_limits: Record<string, number>; min_margin_pct: number | null }
export interface ResolvedPrice {
  product_id: string; uom: string; factor: number; unit_price: number; catalogue_price: number; source: string;
  units: ProductUnit[];
}

export type OrderStatus = "on_hold" | "confirmed" | "partially_delivered" | "delivered" | "cancelled";
export type SyncStatus = "not_synced" | "queued" | "synced" | "failed";
export interface OrderSummary {
  id: string; number: string; status: OrderStatus; order_date: string; customer_po: string | null; currency: string;
  grand_total: number; account: Ref | null; owner: UserRef | null; sync_status: SyncStatus;
  sync_target: AccountingKind | null; external_ref: string | null; delivered_pct: number;
  next_due: string | null; updated_at: string;
}
export interface OrderLine {
  id: string; position: number; description: string; sku: string | null; hsn_code: string | null; uom: string;
  quantity: number; delivered_quantity: number; unit_price: number; discount_pct: number; tax_rate: number;
  net_amount: number; tax_amount: number; line_total: number;
}
export interface OrderSchedule {
  id: string; line_id: string; description: string; due_date: string; quantity: number; note: string | null;
  delivered: boolean; overdue: boolean;
}
export interface OrderDelivery {
  id: string; number: string; delivered_on: string; transporter: string | null; vehicle_number: string | null;
  tracking_number: string | null; notes: string | null; created_by: string | null;
  lines: { line_id: string; description: string; uom: string; quantity: number }[];
}
export interface Invoice {
  id: string; source: "tally" | "zoho_books"; number: string; invoice_date: string; due_date: string | null;
  party_name: string; currency: string; amount: number; balance: number; order_number: string | null; overdue: boolean;
}
export interface Order extends OrderSummary {
  quote: { id: string; number: string } | null; deal: Ref | null; dealer: Ref | null; contact: UserRef | null;
  customer_po_date: string | null; payment_terms: string | null; delivery_terms: string | null; notes: string | null;
  tax_mode: string; tax_mode_label: string; tax_lines: TaxLine[]; gross: number; discount_total: number;
  subtotal: number; tax_total: number; cancelled_at: string | null; cancel_reason: string | null;
  sync_error: string | null; synced_at: string | null; invoiced: number; balance_due: number;
  lines: OrderLine[]; schedules: OrderSchedule[]; deliveries: OrderDelivery[]; invoices: Invoice[];
  hold_reason: string | null; hold_decided_at: string | null; hold_comment: string | null;
}

export type AccountingKind = "tally" | "zoho_books" | "quickbooks" | "xero" | "odoo";
export interface AccountingConnection {
  kind: AccountingKind; connected: boolean; available: boolean; is_active: boolean; auto_push: boolean;
  settings: Record<string, unknown>; agent_token_prefix: string | null; agent_last_seen_at: string | null;
  agent_info: Record<string, unknown>; last_sync_at: string | null; last_error: string | null;
  queued: number; failed: number; synced: number;
}

export interface Territory {
  id: string; name: string; description: string | null; country: string | null; states: string[];
  manager: UserRef | null; account_count: number; dealer_count: number; updated_at: string;
}

export interface DealerMe {
  dealer: Ref; territory: string | null; channel_manager: string | null; channel_manager_email: string | null;
  open_leads: number; won_leads: number; open_orders: number;
}
export interface DealerLead {
  id: string; name: string; customer: string | null; stage: string; status: StageKind; amount: number;
  currency: string; owner: string | null; created_at: string; updated_at: string;
}
export interface DealerOrder {
  id: string; number: string; customer: string | null; order_date: string; status: OrderStatus; currency: string;
  grand_total: number; delivered_pct: number;
}

export type VisitStatus = "planned" | "checked_in" | "completed" | "cancelled";
export type VisitOutcome = "positive" | "neutral" | "negative" | "no_meeting";
export interface Visit {
  id: string; client_ref: string | null; status: VisitStatus; purpose: string; account: Ref;
  contact: UserRef | null; deal: Ref | null; owner: UserRef | null; planned_at: string | null;
  check_in_at: string | null; check_in_lat: number | null; check_in_lng: number | null;
  check_in_accuracy_m: number | null; distance_m: number | null; check_out_at: string | null;
  notes: string | null; outcome: VisitOutcome | null; next_step: string | null; account_city: string | null;
  created_at: string;
}
